import {
  Alert,
  Button,
  DRAWER_TITLE_ID,
  DrawerBody,
  DrawerFooter,
  Icon,
  IconButton,
  Tag,
  Text,
  TextInput,
} from "@growtherapy/sprout-ui";
import { useTranslate } from "@growtherapy/i18n";
import i18next from "i18next";
import {
  ReactNode,
  useEffect,
  useRef,
  useState,
  useCallback,
  MutableRefObject,
  RefObject,
} from "react";
import { Message } from "@twilio/conversations";
import { ChatBubble } from "./ChatBubble";
import { scrollToBottom } from "../utils";
import { DrawerHeader } from "../DrawerHeader";
import { ParticipantStatus } from "../../types";
import {
  faArrowDown,
  faArrowLeftLong,
  faPaperPlaneTop,
} from "@fortawesome/pro-solid-svg-icons";

export interface ChatDrawerWrapperProps {
  identity?: string;
  chatName?: string;
  messages: Message[];
  onSend: (message: string) => void;
  quickReplyComponent?: ReactNode;
  connectedAt?: number;
  footerText?: string;
  headerText?: string;
  warningBannerText?: string;
  emptyState?: ReactNode;
  isEmptyState?: boolean;
  chatStatus?: ParticipantStatus;
  onGoToChatList?: () => void;
  sendIsDisabled?: boolean;
  isError?: boolean;
}
export interface ChatDrawerProps extends ChatDrawerWrapperProps {
  hasUnseenNewMessages: boolean;
  setHasUnseenNewMessages: (hasUnseenNewMessages: boolean) => void;
  scrollContainer: RefObject<HTMLDivElement>;
  isAtBottom: MutableRefObject<boolean>;
  handleScrollToBottom: () => void;
}
function ChatError() {
  const t = useTranslate();
  return (
    <Alert
      className="mb-4"
      heading={t("telehealth-core:chatDrawer.unableToConnectToChat")}
      headingAs="h3"
      use="error"
    />
  );
}

export function ChatDrawerWrapper({
  identity,
  chatName,
  messages,
  onSend,
  quickReplyComponent,
  connectedAt,
  footerText,
  headerText,
  emptyState,
  isEmptyState,
  chatStatus,
  onGoToChatList,
  sendIsDisabled,
  isError,
  warningBannerText,
}: ChatDrawerWrapperProps) {
  const scrollContainer = useRef<HTMLDivElement>(null);
  const isAtBottom = useRef(true);

  const [hasUnseenNewMessages, setHasUnseenNewMessages] =
    useState<boolean>(false);

  const handleScrollToBottom = () => {
    if (scrollContainer.current) {
      scrollToBottom(scrollContainer.current);
      setHasUnseenNewMessages(false);
      isAtBottom.current = true;
    }
  };
  return (
    <ChatDrawer
      identity={identity}
      chatName={chatName}
      messages={messages}
      onSend={onSend}
      quickReplyComponent={quickReplyComponent}
      connectedAt={connectedAt}
      footerText={footerText}
      headerText={headerText}
      emptyState={emptyState}
      isEmptyState={isEmptyState}
      chatStatus={chatStatus}
      onGoToChatList={onGoToChatList}
      sendIsDisabled={sendIsDisabled}
      hasUnseenNewMessages={hasUnseenNewMessages}
      setHasUnseenNewMessages={setHasUnseenNewMessages}
      scrollContainer={scrollContainer}
      isAtBottom={isAtBottom}
      handleScrollToBottom={handleScrollToBottom}
      isError={isError}
      warningBannerText={warningBannerText}
    />
  );
}

export function ChatDrawer({
  identity,
  chatName,
  messages,
  onSend,
  quickReplyComponent,
  connectedAt,
  footerText,
  headerText,
  warningBannerText,
  emptyState,
  isEmptyState,
  chatStatus,
  onGoToChatList,
  sendIsDisabled,
  isError,
  isAtBottom,
  scrollContainer,
  handleScrollToBottom,
  hasUnseenNewMessages,
  setHasUnseenNewMessages,
}: ChatDrawerProps) {
  const t = useTranslate();
  const [messageDraft, setMessageDraft] = useState<string>("");

  const shouldShowDate = !!connectedAt || messages.length > 0;
  const buttonIsDisabled = !messageDraft.trim() || sendIsDisabled || isError;

  /**
   * This - 20 is an arbitrary buffer we use to prevent behavior related to scroll position
   * if the user is slightly above the bottom of the scroll container.
   * It represents 20px
   **/
  const BOTTOM_SCROLL_BUFFER_PX = 20;

  const handleScroll = useCallback(
    () => {
      const container = scrollContainer.current;
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const atBottom =
          scrollTop + clientHeight >= scrollHeight - BOTTOM_SCROLL_BUFFER_PX;
        isAtBottom.current = atBottom;
        if (atBottom) setHasUnseenNewMessages(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(
    function initializeScrollListener() {
      const container = scrollContainer.current;
      const abortController = new AbortController();
      if (container) {
        container.addEventListener("scroll", handleScroll, {
          signal: abortController.signal,
        });
        handleScrollToBottom();
      }
      return () => abortController.abort();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleScroll],
  );

  useEffect(
    function manageScrollOnNewMessages() {
      if (isAtBottom.current) {
        handleScrollToBottom();
      } else {
        setHasUnseenNewMessages(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages],
  );

  const onSendMessage = () => {
    onSend(messageDraft);
    setMessageDraft("");
    if (isAtBottom.current) handleScrollToBottom();
  };

  const screenReaderHeaderTitle = chatName
    ? t("telehealth-core:chatDrawer.chatConversationWithName", { chatName })
    : t("telehealth-core:chatDrawer.chatConversation");
  const screenReaderHeaderStatus = chatStatus
    ? t("telehealth-core:chatDrawer.participantStatus", {
        status: chatStatus.toLowerCase(),
      })
    : "";

  return (
    <>
      <DrawerHeader>
        {onGoToChatList && (
          <button
            className="text-lilac-800 flex items-center"
            onClick={onGoToChatList}
          >
            <Icon icon={faArrowLeftLong} className="mr-2 w-3" aria-hidden />
            <Text as="span" variant="xs" className="font-medium">
              <span className="sr-only">
                {t("telehealth-core:chatDrawer.returnToListOfAllChats")}
              </span>
              <span aria-hidden>
                {t("telehealth-core:chatDrawer.allChats")}
              </span>
            </Text>
          </button>
        )}
        <span className="sr-only" id={DRAWER_TITLE_ID}>
          {screenReaderHeaderTitle + screenReaderHeaderStatus}
        </span>
        <div aria-hidden className="flex items-center w-full">
          <div
            className="fs-exclude"
            data-dd-privacy="mask"
            data-dd-action-name="Click on chat name"
          >
            {chatName || t("telehealth-core:controls.chat")}
          </div>
          {chatStatus && (
            <Tag
              className="ml-2"
              use={
                chatStatus === ParticipantStatus.IN_SESSION
                  ? "green"
                  : "neutral"
              }
            >
              {chatStatus}
            </Tag>
          )}
        </div>
      </DrawerHeader>
      {headerText && (
        <div className="px-8 py-3">
          <Text variant="xs" className="text-neutral-700">
            {headerText}
          </Text>
        </div>
      )}
      {isEmptyState ? (
        <DrawerBody>{emptyState}</DrawerBody>
      ) : (
        <>
          <DrawerBody
            className="bg-neutral-400 h-full overflow-y-auto flex flex-col"
            ref={scrollContainer}
            aria-label={t(
              "telehealth-core:chatDrawer.chatConversationWithName",
              { chatName },
            )}
          >
            <Text
              variant="xs"
              className="block text-neutral-700 text-center pb-6"
            >
              {connectedAt &&
                chatStatus === ParticipantStatus.IN_SESSION &&
                `Session started at ${new Date(connectedAt).toLocaleTimeString(
                  i18next.language,
                  {
                    hour: "numeric",
                    minute: "numeric",
                  },
                )}, `}
              {shouldShowDate &&
                new Date().toLocaleString(i18next.language, {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
            </Text>
            <ul
              aria-live="polite"
              aria-label={t("telehealth-core:chatDrawer.chatMessages")}
            >
              {messages.map((message) => {
                const isByCurrentUser = message.author === identity;
                return (
                  <ChatBubble
                    key={message.sid}
                    message={message}
                    isSelf={isByCurrentUser}
                  />
                );
              })}
            </ul>
            {!messages.length ? (
              <span className="sr-only">
                {t("telehealth-core:chatDrawer.noMessages")}
              </span>
            ) : null}
            {footerText && (
              <div className="pt-6 mt-auto">
                <Text variant="xs" className="text-neutral-700 text-center">
                  {footerText}
                </Text>
              </div>
            )}
          </DrawerBody>
          {quickReplyComponent}
          {hasUnseenNewMessages && (
            <div className="border-t border-neutral-600 px-8 py-3 flex items-center justify-center">
              <Button onClick={handleScrollToBottom} use="secondary">
                {t("telehealth-core:chatDrawer.seeLatestMessage")}{" "}
                <Icon icon={faArrowDown} />
              </Button>
            </div>
          )}
          {warningBannerText && (
            <div className="border-t border-neutral-600 px-8 py-5 flex items-center justify-center bg-neutral-300">
              <Text variant="xs" className="text-neutral-700 leading-relaxed">
                {warningBannerText}
              </Text>
            </div>
          )}
          <div className="bg-neutral-200 rounded-b-xl">
            <DrawerFooter>
              <div className="flex flex-col w-full">
                {isError && <ChatError />}
                <form
                  className="flex w-full"
                  onSubmit={(event) => event.preventDefault()}
                >
                  <TextInput
                    onChange={(event) => setMessageDraft(event.target.value)}
                    placeholder={t(
                      "telehealth-core:chatDrawer.composeYourMessage",
                    )}
                    className="mr-2 !bg-neutral-100"
                    mask="exclude"
                    value={messageDraft}
                  />

                  <Button
                    disabled={buttonIsDisabled}
                    onClick={onSendMessage}
                    type="submit"
                    className="hidden sm:block"
                  >
                    <span aria-hidden>
                      {t("telehealth-core:chatDrawer.send")}
                    </span>
                    <span className="sr-only">
                      {chatName
                        ? t("telehealth-core:chatDrawer.sendMessageToName", {
                            chatName,
                          })
                        : t("telehealth-core:chatDrawer.sendMessage")}
                    </span>
                  </Button>
                  <IconButton
                    disabled={buttonIsDisabled}
                    onClick={onSendMessage}
                    type="submit"
                    className="sm:hidden fs-exclude"
                    data-dd-privacy="mask"
                    data-dd-action-name="Click on send message button"
                    aria-label={
                      chatName
                        ? t("telehealth-core:chatDrawer.sendMessageToName", {
                            chatName,
                          })
                        : t("telehealth-core:chatDrawer.sendMessage")
                    }
                    iconDefinition={faPaperPlaneTop}
                  />
                </form>
              </div>
            </DrawerFooter>
          </div>
        </>
      )}
    </>
  );
}
